import { PlatformError } from './errors.ts';

/**
 * Stable JSON canonicalisation (RFC 8785 / JCS compatible for JSON values):
 * object keys sorted by UTF-16 code units, no whitespace, `undefined` properties omitted,
 * `toJSON()` honoured (Dates become ISO strings), numbers in ECMAScript shortest form.
 * Throws `validation_failed` for non-finite numbers, bigint, functions, symbols and top-level undefined.
 */
export function canonicalJson(value: unknown): string {
  return serialise(value, '$');
}

function serialise(value: unknown, path: string): string {
  if (value === null) return 'null';
  switch (typeof value) {
    case 'string':
      return JSON.stringify(value);
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      if (!Number.isFinite(value)) {
        throw new PlatformError('validation_failed', `Cannot canonicalise non-finite number at ${path}`);
      }
      return JSON.stringify(value);
    case 'object': {
      const withToJson = value as { toJSON?: unknown };
      if (typeof withToJson.toJSON === 'function') {
        return serialise((withToJson.toJSON as () => unknown).call(value), path);
      }
      if (Array.isArray(value)) {
        return `[${value
          .map((item, i) => (item === undefined ? 'null' : serialise(item, `${path}[${i}]`)))
          .join(',')}]`;
      }
      if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
        throw new PlatformError('validation_failed', `Cannot canonicalise binary data at ${path}`);
      }
      const record = value as Record<string, unknown>;
      const keys = Object.keys(record)
        .filter((k) => record[k] !== undefined)
        .sort();
      return `{${keys.map((k) => `${JSON.stringify(k)}:${serialise(record[k], `${path}.${k}`)}`).join(',')}}`;
    }
    default:
      throw new PlatformError('validation_failed', `Cannot canonicalise ${typeof value} at ${path}`);
  }
}

/** SHA-256 hex of `canonicalJson(value)`. Used for `argsHash`, `proposalHash`, payload hashes. */
export function hashCanonical(value: unknown): string {
  return sha256Hex(canonicalJson(value));
}

const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
  0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
  0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
  0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
];

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/**
 * Synchronous SHA-256 (FIPS 180-4) in pure TypeScript so contracts work unchanged in the browser
 * and in Bun. Strings are UTF-8 encoded.
 */
export function sha256Hex(input: string | Uint8Array): string {
  const data = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const paddedLength = Math.ceil((data.length + 9) / 64) * 64;
  const buffer = new Uint8Array(paddedLength);
  buffer.set(data);
  buffer[data.length] = 0x80;
  const view = new DataView(buffer.buffer);
  const bitLength = data.length * 8;
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);

  const w = new DataView(new ArrayBuffer(256));
  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) w.setUint32(i * 4, view.getUint32(offset + i * 4));
    for (let i = 16; i < 64; i++) {
      const x15 = w.getUint32((i - 15) * 4);
      const x2 = w.getUint32((i - 2) * 4);
      const s0 = rotr(x15, 7) ^ rotr(x15, 18) ^ (x15 >>> 3);
      const s1 = rotr(x2, 17) ^ rotr(x2, 19) ^ (x2 >>> 10);
      w.setUint32(i * 4, (w.getUint32((i - 16) * 4) + s0 + w.getUint32((i - 7) * 4) + s1) >>> 0);
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + (K[i] ?? 0) + w.getUint32(i * 4)) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }
  return [h0, h1, h2, h3, h4, h5, h6, h7].map((x) => x.toString(16).padStart(8, '0')).join('');
}

/**
 * A UUID (version 8, RFC 9562) derived from `seed`: the same seed always gives the same id (workflow replays,
 * the start command of a task, the run id the starter gives a task). Moved here from core-workflows by 09 so
 * intake and approvals can derive the same ids; core-workflows re-exports it.
 */
export function deterministicId(seed: string): string {
  const h = sha256Hex(seed);
  const variant = ['8', '9', 'a', 'b'][Number.parseInt(h.charAt(16), 16) & 3] ?? '8';
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-8${h.slice(13, 16)}-${variant}${h.slice(17, 20)}-${h.slice(20, 32)}`;
}
